/**
 * „Verweigerte Bestellungen" (GF-Wunsch 27.10.2026): wie entwickeln sich die Bestellungen, die PL-X nicht bestätigt,
 * und woran liegt es. Je Kalenderwoche die verweigerten AUF DER NULLLINIE (vergleichbar über die Wochen), die Anfos
 * darüber, Umschalter auf den Anteil; darunter die Tabelle mit der Aufteilung „Adresse hatte schon bestellt" /
 * „ohne frühere Bestellung". Den eigentlichen Grund kennt nur PL-X — die Karte nennt deren Zahlen mit Stand, statt
 * einen Grund zu erfinden. Quoten der Sektion bleiben auf Anfos bezogen; „Anteil verweigert" ist die einzige Zahl mit
 * Bestellungen im Nenner und sagt das.
 *
 * P5-Review #24 (dataviz): verweigert nicht mehr orange OBEN auf dem Anfo-Balken (orange = Direkt-Mailing in der
 * Sektion, die Lage hing an der Anfo-Höhe); Statusfarbe „kritisch" unten, Anfos neutral grau darüber. Laufende Woche
 * blass, Achse ohne Drehung, Luft über dem höchsten Balken, Köpfe wie in den Übersichten (`BESTELL_KOEPFE`).
 */
import { FC, useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography, useMediaQuery, useTheme } from '@mui/material';
import { BarDatum, ResponsiveBar } from '@nivo/bar';
import ChartCard from '../../../components/auswertungen/ChartCard';
import SortableTable, { Spalte, spaltenMeta } from '../../../components/auswertungen/SortableTable';
import SpaltenMenue from '../../../components/auswertungen/SpaltenMenue';
import { Breite, useSpaltenwahl } from '../../../components/auswertungen/spaltenwahl';
import { BefundZeile, Hinweis, Kachel, int, Laden, useLaden } from '../../../components/auswertungen/kennzahlTeile';
import { Legende, TipRow, achse, unsicherFuellung, unsicherKonturen, useVizStyle } from '../../../components/auswertungen/vizStyle';
import { formatPct, TERMS } from '../../../helpers/kpiDefinitions';
import { fetchVerweigerteBestellungen, VerweigerungWoche } from '../../../helpers/verweigerungData';
import { BESTELL_KOEPFE, VERWEIGERT_ANTEIL_MIN_BESTELLUNGEN, verweigertAnteilGrau } from './UebersichtKarte';
import { ohneFruehereJeWoche, verweigertBefund } from './verweigertBefund';
import { formatNumber } from '../../../helpers/numberUtils';

const BEGRIFFE = {
  bestellungen: TERMS.bestellungenAlle,
  verweigert: TERMS.verweigert,
  anteil: TERMS.verweigertAnteil,
  mitVorbestellung: TERMS.verweigertSchonBestellt,
};

/**
 * Farben: verweigert = Statusfarbe „kritisch" der dataviz-Referenzpalette (in beiden Modi dieselbe, Kontrast 4,7 hell /
 * 3,6 dunkel), Anfos = neutrales Grau (`viz.muted`) — bewusst keine Kanalfarbe. Geprüft 27.10.2026 mit
 * `validate_palette.js "#d03b3b,#898781"`: CVD ΔE 9,1, Normalsicht 18,9, Kontrast ≥ 3:1 in beiden Modi (der Chroma-Hinweis
 * zum Grau ist gewollt: Anfos sind hier Kontext, keine Kategorie).
 */
const ROT = '#d03b3b';

type Ansicht = 'anzahl' | 'anteil';

/** D16: Standardspalten je Kartenbreite; am Handy Woche, verweigert und Anteil (vier Spalten liefen bei 390 px über). */
const STANDARDS: Record<Breite, readonly string[]> = {
  breit: ['kw', 'bestellungen', 'verweigert', 'anteil', 'anfos', 'mit', 'ohne'],
  mittel: ['kw', 'bestellungen', 'verweigert', 'anteil', 'anfos', 'mit', 'ohne'],
  schmal: ['kw', 'verweigert', 'anteil'],
};

const kurz = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}.`;
const gross = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

const VerweigerteKarte: FC = () => {
  const { data, fehler } = useLaden(fetchVerweigerteBestellungen);
  const viz = useVizStyle();
  const theme = useTheme();
  const [ansicht, setAnsicht] = useState<Ansicht>('anzahl');

  const balken = useMemo(
    () =>
      (data?.wochen ?? []).map((w) => ({
        woche: String(w.kw),
        verweigert: w.verweigert,
        anfos: w.anfos,
        anteil: w.anteil_verweigert ?? 0,
        blass: w.laufend,
        w,
      })),
    [data],
  );
  const hoechst = useMemo(
    () => Math.max(1, ...balken.map((b) => (ansicht === 'anteil' ? b.anteil : b.verweigert + b.anfos))),
    [balken, ansicht],
  );
  const befund = useMemo(() => (data ? verweigertBefund(data.wochen) : null), [data]);

  const spalten: Spalte<VerweigerungWoche>[] = [
    {
      key: 'kw',
      label: 'Woche',
      pflicht: true,
      // Zwei Zeilen statt „KW 43 · 19.10.–25.10." in einer: bei 390 px belegte die feste Spalte 150 von 267 px.
      render: (w) => (
        <Box sx={{ lineHeight: 1.25 }}>
          KW {w.kw}
          {w.laufend ? ' (läuft)' : ''}
          <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary' }}>
            {kurz(w.von)}–{kurz(w.bis)}
          </Box>
        </Box>
      ),
      sortValue: (w) => w.von,
      summary: data ? (
        <Box sx={{ lineHeight: 1.25 }}>
          Summe
          <Box component="span" sx={{ display: 'block', fontSize: 11, color: 'text.secondary', fontWeight: 400 }}>
            {kurz(data.summe.von)}–{kurz(data.summe.bis)}
          </Box>
        </Box>
      ) : undefined,
      minWidth: 92,
    },
    {
      key: 'bestellungen',
      ...BESTELL_KOEPFE.bestellungen,
      kopfGruppe: 'Bestellungen',
      align: 'right',
      render: (w) => int(w.bestellungen),
      sortValue: (w) => w.bestellungen,
      summary: int(data?.summe.bestellungen),
    },
    {
      key: 'verweigert',
      ...BESTELL_KOEPFE.verweigert,
      kopfGruppe: 'Bestellungen',
      align: 'right',
      render: (w) => int(w.verweigert),
      sortValue: (w) => w.verweigert,
      summary: int(data?.summe.verweigert),
    },
    {
      key: 'anteil',
      ...BESTELL_KOEPFE.anteil,
      kopfGruppe: 'Bestellungen',
      align: 'right',
      hinweis: `Grau unter ${VERWEIGERT_ANTEIL_MIN_BESTELLUNGEN} Bestellungen.`,
      unsicher: (w) => verweigertAnteilGrau(w.bestellungen),
      render: (w) => (w.anteil_verweigert === null ? '–' : formatPct(w.anteil_verweigert)),
      sortValue: (w) => w.anteil_verweigert,
      summary: data?.summe.anteil_verweigert == null ? '–' : formatPct(data.summe.anteil_verweigert),
    },
    {
      key: 'anfos',
      ...BESTELL_KOEPFE.anfos,
      kopfGruppe: 'Anfos',
      align: 'right',
      render: (w) => int(w.anfos),
      sortValue: (w) => w.anfos,
      summary: int(data?.summe.anfos),
    },
    {
      key: 'mit',
      label: BEGRIFFE.mitVorbestellung.short,
      tip: BEGRIFFE.mitVorbestellung.tip,
      einheit: `≤ ${data?.vorbestellung_tage ?? 90} Tage davor`,
      kopfGruppe: 'davon verweigert',
      align: 'right',
      render: (w) => int(w.verweigert_mit_vorbestellung),
      sortValue: (w) => w.verweigert_mit_vorbestellung,
      summary: int(data?.summe.verweigert_mit_vorbestellung),
    },
    {
      key: 'ohne',
      label: 'ohne frühere',
      tip: `Verweigerte, deren Adresse in den ${data?.vorbestellung_tage ?? 90} Tagen davor nicht bestellt hatte — hier liegen am ehesten Bonitäts- und manuelle Ablehnungen, also die Fälle für ein anderes Angebot (z. B. Vorkasse).`,
      einheit: 'Bestellung',
      kopfGruppe: 'davon verweigert',
      align: 'right',
      render: (w) => int(w.verweigert_ohne_vorbestellung),
      sortValue: (w) => w.verweigert_ohne_vorbestellung,
      summary: int(data?.summe.verweigert_ohne_vorbestellung),
    },
  ];

  const meta = spaltenMeta(spalten);
  const wahl = useSpaltenwahl('bestellungenVerweigert', meta, STANDARDS);
  const sichtbar = new Set(wahl.sichtbar);

  const anteilSicht = ansicht === 'anteil';
  // Schmal steht der Umschalter über dem Diagramm statt neben dem Titel (sonst bleiben Titel und Untertitel ~150 px).
  const schmal = useMediaQuery(theme.breakpoints.down('sm'));
  const umschalter = (
    <ToggleButtonGroup size="small" exclusive value={ansicht} onChange={(_e, v: Ansicht | null) => v && setAnsicht(v)} aria-label="Ansicht">
      <ToggleButton value="anzahl" sx={{ textTransform: 'none', px: 1.25 }}>
        Anzahl
      </ToggleButton>
      <ToggleButton value="anteil" sx={{ textTransform: 'none', px: 1.25, whiteSpace: 'nowrap' }}>
        Anteil %
      </ToggleButton>
    </ToggleButtonGroup>
  );
  return (
    <ChartCard
      title="Verweigerte Bestellungen"
      subtitle={`letzte ${data?.wochen.length ?? 13} Wochen — unabhängig vom Zeitraum oben`}
      actions={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SpaltenMenue spalten={meta} wahl={wahl} />
          {!schmal && umschalter}
        </Box>
      }
      howToRead={
        <>
          Je Kalenderwoche alle Bestellungen: unten die von PL-X verweigerten, darüber die Anfos. „Anteil %“ zeigt die
          verweigerten als Anteil an den Bestellungen der Woche. Alle anderen Kennzahlen der Sektion rechnen nur mit Anfos.
          Die Tabelle zeigt bei den verweigerten, ob dieselbe Adresse kurz davor schon bestellt hatte — das sind meist
          Mehrfachbestellungen bestehender Kunden, kein verlorener Neukunde.
        </>
      }
      terms={[BEGRIFFE.bestellungen, BEGRIFFE.verweigert, BEGRIFFE.anteil, BEGRIFFE.mitVorbestellung]}
      source="PL-X-Bestellungen (plx_all_orders), Vertragsstatus V; ohne Testbestellungen"
    >
      <Box ref={wahl.messRef}>
      {!data ? (
        <Laden fehler={fehler} />
      ) : (
        <>
          {schmal && <Box sx={{ mb: 1.5 }}>{umschalter}</Box>}
          {befund && <BefundZeile text={'text' in befund ? befund.text : undefined} fehlt={'fehlt' in befund ? befund.fehlt : null} tip={TERMS.verweigertBefund.tip} />}
          {/*
            A6: Hauptkachel ist der Teil, bei dem ein Kauf verloren gehen kann — Verweigerte ohne frühere Bestellung; alle
            Bestellungen und alle Verweigerten stehen klein daneben.
          */}
          {(() => {
            const o = ohneFruehereJeWoche(data.wochen);
            return (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-end', gap: 2, mb: 2 }}>
                <Box sx={{ flex: '0 1 320px', minWidth: 0 }}>
                  <Kachel
                    wert={int(o.summe)}
                    label={`${TERMS.verweigertOhneFruehere.label} in ${data.wochen.length} Wochen`}
                    tip={TERMS.verweigertOhneFruehere.tip}
                    sub={o.jeWoche === null ? undefined : `≈ ${formatNumber(o.jeWoche, o.jeWoche < 10 ? 1 : 0)} je Woche (Ø ${o.wochen} abgeschlossene Wochen)`}
                  />
                </Box>
                <Box sx={{ fontSize: 13, color: 'text.secondary', lineHeight: 1.6, pb: 0.5 }}>
                  von {int(data.summe.verweigert)} verweigerten
                  {data.summe.anteil_verweigert == null ? '' : ` (${formatPct(data.summe.anteil_verweigert)} der Bestellungen)`} ·{' '}
                  {int(data.summe.verweigert_mit_vorbestellung)} hatten schon bestellt
                  <br />
                  {int(data.summe.bestellungen)} {BEGRIFFE.bestellungen.label} bis {kurz(data.summe.bis)}
                </Box>
              </Box>
            );
          })()}
          <Legende
            items={
              anteilSicht
                ? [{ label: `${gross(BEGRIFFE.verweigert.short)} in % der Bestellungen`, color: ROT }]
                : [
                    { label: gross(BEGRIFFE.verweigert.short), color: ROT },
                    { label: TERMS.anfos.label, color: viz.muted },
                  ]
            }
          />
          <Box sx={{ height: 260, mb: 1 }}>
            <ResponsiveBar
              data={balken as unknown as BarDatum[]}
              keys={anteilSicht ? ['anteil'] : ['verweigert', 'anfos']}
              indexBy="woche"
              groupMode="stacked"
              margin={{ top: 8, right: 8, bottom: 44, left: 44 }}
              padding={0.3}
              valueScale={{ type: 'linear', min: 0, max: hoechst * 1.1 }}
              colors={(b) => {
                const f = b.id === 'anfos' ? viz.muted : ROT;
                return b.data.blass ? unsicherFuellung(f) : f;
              }}
              borderWidth={1}
              borderColor={theme.palette.background.default}
              theme={viz.nivo}
              animate={false}
              enableLabel={false}
              enableGridY
              axisLeft={{ tickSize: 0, tickPadding: 6, tickValues: 5, format: achse(anteilSicht ? '%' : '') }}
              axisBottom={{ tickSize: 0, tickPadding: 6, tickRotation: 0, legend: 'Kalenderwoche', legendPosition: 'middle', legendOffset: 34 }}
              layers={[
                'grid',
                'axes',
                'bars',
                // Laufende Woche (wie im Verlauf der Kanäle, D6): nicht fertig, kein Einbruch — Deckkraft + Kontur.
                unsicherKonturen((b) => ((b.data.data as unknown as { blass: boolean }).blass ? (b.data.id === 'anfos' ? viz.muted : ROT) : null)),
              ]}
              tooltip={({ data: d }) => {
                const w = (d as unknown as { w: VerweigerungWoche }).w;
                return (
                  <Box>
                    <Box sx={{ fontWeight: 600, mb: 0.5 }}>
                      KW {w.kw} · {kurz(w.von)}–{kurz(w.bis)}
                      {w.laufend ? ' (läuft, nicht fertig)' : ''}
                    </Box>
                    <TipRow label={BEGRIFFE.bestellungen.label} value={int(w.bestellungen)} />
                    <TipRow color={ROT} label={gross(BEGRIFFE.verweigert.short)} value={`${int(w.verweigert)}${w.anteil_verweigert == null ? '' : ` (${formatPct(w.anteil_verweigert)})`}`} />
                    <TipRow color={viz.muted} label={TERMS.anfos.label} value={int(w.anfos)} />
                    <TipRow label="davon verweigert, schon bestellt" value={int(w.verweigert_mit_vorbestellung)} />
                  </Box>
                );
              }}
              role="img"
              ariaLabel={anteilSicht ? 'Anteil verweigerter Bestellungen je Kalenderwoche' : 'Verweigerte Bestellungen und Anfos je Kalenderwoche'}
            />
          </Box>
          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 1 }}>
            Blass: laufende Woche, bis zum PL-X-Datenstand — nicht fertig, kein Einbruch.
          </Typography>
          <SortableTable
            rows={[...data.wochen].reverse()}
            columns={spalten.filter((c) => sichtbar.has(c.key))}
            rowKey={(w) => w.von}
            initialSort={{ key: 'kw', dir: 'desc' }}
            muted={(w) => w.laufend}
            // Die laufende (blasse) Woche steht in beiden Richtungen am Ende, nicht beim Aufsteigend-Sortieren oben.
            mutedLast
            summaryLabel="Summe"
            maxHeight={null}
            festeErsteSpalte
            dense
          />
          <Hinweis>
            Den Grund einer Verweigerung kennt nur PL-X (PX-Data, „Vertragsbestätigungsanalyse“). Dort waren es 2026 bis
            30.09. 342 Verweigerungen: 138 Mehrfachbesteller, 126 Mindestabstand (vermutlich: eine neue Bestellung derselben
            Adresse kam zu kurz nach der vorigen — PL-X definiert den Begriff nicht), 53 Bonität, 25 manuell. Nur die Bonitätsfälle sind Kandidaten für ein anderes
            Angebot (z. B. Vorkasse); die beiden größten Gruppen sind Adressen, die vorher schon bestellt hatten.
          </Hinweis>
        </>
      )}
      </Box>
    </ChartCard>
  );
};

export default VerweigerteKarte;
